import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api from '../config/api';
import { isActiveMemberForSeason } from '../utils/memberStatus';

const AuthContext = createContext();

function normalizeAuthUser(freshUser) {
  if (!freshUser) return null;
  return {
    ...freshUser,
    id: freshUser.id || freshUser._id,
  };
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const response = await api.get('/auth/me');
      const normalized = normalizeAuthUser(response.data?.user);
      if (!normalized) return null;
      localStorage.setItem('user', JSON.stringify(normalized));
      setUser(normalized);
      return normalized;
    } catch {
      // Keep cached user if refresh fails (offline, expired token, etc.)
      return null;
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token) {
      setLoading(false);
      return;
    }

    if (userData) {
      try {
        setUser(JSON.parse(userData));
      } catch {
        localStorage.removeItem('user');
      }
    }

    fetchCurrentUser().finally(() => setLoading(false));
  }, [fetchCurrentUser]);

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', { email, password });
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      await fetchCurrentUser();

      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Login failed' 
      };
    }
  };

  const loginWithToken = async (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    await fetchCurrentUser();
    return { success: true };
  };

  const register = async (userData) => {
    try {
      // Si les données contiennent des fichiers, utiliser FormData
      let requestData;
      let headers = {};
      
      // Vérifier si des fichiers sont présents
      const hasFiles = userData.identityFileF || 
                      userData.identityFileBack || 
                      userData.tesseraFile || 
                      userData.blackWhiteFile || 
                      userData.parentIdentityFile;
      
      if (hasFiles) {
        // Utiliser FormData pour l'upload de fichiers
        requestData = new FormData();
        
        // Ajouter tous les champs au FormData
        Object.keys(userData).forEach(key => {
          // Ignorer les fichiers null/undefined
          if (userData[key] === null || userData[key] === undefined) {
            return;
          }
          
          if (userData[key] instanceof File) {
            requestData.append(key, userData[key]);
          } else if (typeof userData[key] === 'object' && userData[key] !== null) {
            // Stringify les objets (mais pas les fichiers)
            requestData.append(key, JSON.stringify(userData[key]));
          } else {
            requestData.append(key, userData[key]);
          }
        });
        
        // Ne pas définir de headers - l'intercepteur axios supprimera automatiquement Content-Type pour FormData
        headers = {};
      } else {
        requestData = userData;
      }
      
      const response = await api.post('/auth/register', requestData, { headers });
      const { token, user } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setUser(user);

      await fetchCurrentUser();

      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      console.error('Error response:', error.response?.data);

      // 413 = charge utile trop grande (souvent nginx client_max_body_size, ou limite API)
      if (error.response?.status === 413) {
        const msg =
          (typeof error.response?.data === 'object' && error.response?.data?.error) ||
          null;
        return {
          success: false,
          errorKey: 'payloadTooLarge',
          error: msg || undefined,
        };
      }

      // Gérer les erreurs de validation
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const validationErrors = error.response.data.errors.map(err => {
          const fieldName = err.path || err.param || 'Champ';
          const message = err.msg || err.message || 'Erreur de validation';
          // Traduire les noms de champs en français
          const fieldTranslations = {
            'email': 'Email',
            'password': 'Mot de passe',
            'civilite': 'Civilité',
            'firstName': 'Prénom',
            'lastName': 'Nom',
            'sexe': 'Sexe',
            'dateOfBirth': 'Date de naissance',
            'phone': 'Téléphone'
          };
          const translatedField = fieldTranslations[fieldName] || fieldName;
          return `${translatedField}: ${message}`;
        });
        return { 
          success: false, 
          error: validationErrors,
          errors: validationErrors // Garder aussi un tableau pour faciliter l'affichage
        };
      }
      
      // Gérer les autres erreurs
      const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Inscription échouée';
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isActiveMember = () => isActiveMemberForSeason(user);

  const value = {
    user,
    loading,
    login,
    loginWithToken,
    register,
    logout,
    isAdmin,
    isActiveMember,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


